/**
 * Helpers for the per-step `data` key/value rows and for the fields a step type
 * does not render.
 *
 * Background rows
 * ---------------
 * A step's optional background override travels to the runtime engine as a row
 * in the step's `data` table (`StepDataRows` in editor-api). The publish export
 * flattens those rows into an object map on `step.data`, and the client reads
 * `step.data.background` and treats it as a SCENE ALIAS — i.e. the `alias` of a
 * Location on the book, the same string the branch-level `scene` carries.
 *
 * See editor-api/src/controllers/story_export.js (`prepareStep`, the "Data Rows"
 * block) for the export shape, and the client's `Story.loadState` for the read.
 *
 * Unrendered fields
 * -----------------
 * The editor renders a step's inputs from the `fields` list on its StepType row.
 * A type whose list omits `"text"` renders no text input, so `serialize(form)`
 * produces no `text` key for it and a save would recreate the step without its
 * text. `preserveUnrenderedStepText` restores those values.
 */

export const STEP_BACKGROUND_DATA_KEY = 'background';

export type StepDataRow = {
  key: string;
  value: string;
};

type StepTypeLike = {
  id: number | string;
  fields?: unknown;
};

type FormStepLike = {
  id?: number | string;
  stepTypeId?: number | string;
  text?: string;
  [key: string]: unknown;
};

type StateStepLike = {
  id?: number | string;
  stepTypeId?: number | string;
  text?: string;
  [key: string]: unknown;
};

type LocationLike = {
  alias?: string;
  title?: string;
  videoId?: number | null;
};

/**
 * `step.data` reaches the form as an array of `{ key, value }` rows, but legacy
 * and freshly-built steps can carry `{}`, `null` or `undefined`. Normalise to an
 * array so callers never have to branch.
 */
export function normalizeStepData(data: unknown): StepDataRow[] {
  if (!Array.isArray(data)) {
    return [];
  }
  return data
    .filter((row): row is StepDataRow => !!row && typeof row === 'object')
    .map((row) => ({
      ...row,
      key: row.key ?? '',
      value: row.value ?? '',
    }));
}

/**
 * The alias effectively set as this step's background override, or '' when the
 * step has none (i.e. it inherits the node's location).
 *
 * Reads the LAST background row, because that is the one the reader actually
 * gets: the publish export flattens data rows into an object map key by key, so
 * with duplicate keys the last assignment wins and the earlier rows are shadowed
 * and inert. `writeStepBackground` canonicalises to at most one row, so
 * duplicates only exist until the author next touches the background — but while
 * they do, the picker, the save-time checks and the publish gate must all agree
 * with the export about which value is real.
 */
export function readStepBackground(data: unknown): string {
  const rows = normalizeStepData(data)
    .filter((item) => item.key === STEP_BACKGROUND_DATA_KEY);
  return rows.length > 0 ? rows[rows.length - 1].value ?? '' : '';
}

/**
 * Set, replace or clear this step's background row, leaving every other data row
 * and its ordering untouched.
 *
 * Canonicalises to AT MOST ONE background row: every existing one is removed and
 * a single row is reinserted at the position the first one held. That matters
 * because the raw Data table lets an author add a second `background` row by
 * hand, and the export flattens rows into an object map where the last one would
 * silently win over the one the picker displays.
 *
 * An empty alias removes the row(s) entirely so the step falls back to the node's
 * location rather than exporting `background: ""`.
 */
export function writeStepBackground(data: unknown, alias: string): StepDataRow[] {
  const rows = normalizeStepData(data);
  const firstIndex = rows.findIndex((item) => item.key === STEP_BACKGROUND_DATA_KEY);
  const withoutBackground = rows.filter((item) => item.key !== STEP_BACKGROUND_DATA_KEY);

  if (!alias) {
    return withoutBackground;
  }

  const row = { key: STEP_BACKGROUND_DATA_KEY, value: alias };

  if (firstIndex === -1) {
    return [...withoutBackground, row];
  }

  // Every row before firstIndex is a non-background row, so it survived the
  // filter and still sits at the same position: splicing there restores the
  // original placement.
  const result = withoutBackground.slice();
  result.splice(firstIndex, 0, row);
  return result;
}

export type StepBackgroundOption = {
  label: string;
  value: string;
  type: 'none' | 'image' | 'video';
  alias?: string;
};

export type StepBackgroundGroup = {
  label: string;
  options: StepBackgroundOption[];
};

/**
 * The options the per-step background picker offers.
 *
 * Pure, and kept out of the component, because the one rule that matters here is
 * a correctness rule rather than a presentation one: when `allowVideoLocations`
 * is false — which is exactly when the step is a Video step — the Video group is
 * not built at all, so the picker cannot be used to author the video-step /
 * video-background conjunction. `findVideoBackgroundConflicts` is the save-time
 * half of the same rule, for values that arrive by other routes.
 *
 * PHASE B: relaxing this belongs with the node-level guard's relax, not before.
 */
export function buildStepBackgroundOptions(
  locations: LocationLike[] | undefined | null,
  allowVideoLocations: boolean,
): StepBackgroundGroup[] {
  const selectable = (Array.isArray(locations) ? locations : [])
    .filter((location) => !!location.alias);

  const groups: StepBackgroundGroup[] = [
    {
      label: 'Default',
      options: [{ label: 'Node location', value: '', type: 'none' }],
    },
    {
      label: 'Images',
      options: selectable
        .filter((location) => !location.videoId)
        .map((location) => ({
          label: location.title ?? location.alias ?? '',
          value: location.alias ?? '',
          type: 'image' as const,
          alias: location.alias,
        })),
    },
  ];

  if (allowVideoLocations) {
    groups.push({
      label: 'Video',
      options: selectable
        .filter((location) => !!location.videoId)
        .map((location) => ({
          label: location.title ?? location.alias ?? '',
          value: location.alias ?? '',
          type: 'video' as const,
          alias: location.alias,
        })),
    });
  }

  return groups;
}

/**
 * Does the editor render a text input for this step type? Driven by the
 * StepType's `fields` list exactly as StepsForm does, so the answer here and the
 * form's rendering decision cannot drift.
 */
export function stepTypeRendersText(
  stepTypes: StepTypeLike[] | undefined | null,
  stepTypeId: number | string | undefined,
): boolean {
  if (!Array.isArray(stepTypes) || stepTypeId === undefined || stepTypeId === null) {
    return false;
  }
  const stepType = stepTypes.find((type) => Number(type.id) === Number(stepTypeId));
  if (!stepType || !Array.isArray(stepType.fields)) {
    return false;
  }
  return stepType.fields.includes('text');
}

/**
 * Restore `text` on saved steps whose type renders no text input.
 *
 * `serialize()` reads the form DOM and, with `empty` unset, omits inputs whose
 * value is ''. So a missing `text` key is ambiguous: it is either a type that
 * renders no text field at all, or a text-bearing step the author deliberately
 * cleared. Three conditions separate the case worth restoring from the rest:
 *
 * 1. The step must already exist in the database (it has an `id`). A brand new
 *    step has no persisted text, so there is nothing to preserve.
 * 2. Its type must be UNCHANGED since load. Retyping a step into a type that
 *    renders no text is a deliberate act, and `edit_context.js` clears text on
 *    exactly that transition ("clear incompatible fields"); preserving it here
 *    would contradict the established type-change semantics.
 * 3. The CURRENT type must render no text input. A type that does render one
 *    keeps whatever `serialize()` said, so clearing text still works.
 *
 * `originalSteps` is the pristine copy of the steps as loaded (AddBranch's
 * `stepConst`), joined BY ID rather than by index so that deleting or reordering
 * steps before saving cannot copy one step's text onto another.
 */
export function preserveUnrenderedStepText<T extends FormStepLike>(
  formSteps: T[] | undefined | null,
  stateSteps: StateStepLike[] | undefined | null,
  stepTypes: StepTypeLike[] | undefined | null,
  originalSteps?: StateStepLike[] | null,
): T[] | undefined | null {
  if (!Array.isArray(formSteps) || !Array.isArray(stateSteps)) {
    return formSteps;
  }

  const originalsById = new Map<string, StateStepLike>();
  if (Array.isArray(originalSteps)) {
    originalSteps.forEach((step) => {
      if (step && step.id !== undefined && step.id !== null) {
        originalsById.set(String(step.id), step);
      }
    });
  }

  return formSteps.map((formStep, i) => {
    if (formStep?.text !== undefined) {
      return formStep;
    }

    const stateStep = stateSteps[i];

    // formSteps[i] and stateSteps[i] are the same step by construction: the input
    // names carry the state index literally (`steps[${stepIndex}][...]`) and
    // form-serialize assigns to that array position, so this join does not depend
    // on DOM order, drag/drop or render timing.
    const stepId = formStep?.id ?? stateStep?.id;
    if (stepId === undefined || stepId === null) {
      return formStep;
    }

    const original = originalsById.get(String(stepId));
    if (!original || !original.text) {
      return formStep;
    }

    const currentTypeId = formStep?.stepTypeId ?? stateStep?.stepTypeId;
    if (Number(original.stepTypeId) !== Number(currentTypeId)) {
      return formStep;
    }

    if (stepTypeRendersText(stepTypes, currentTypeId)) {
      return formStep;
    }

    return { ...formStep, text: original.text };
  });
}

function findLocationByAlias(
  locations: LocationLike[] | undefined | null,
  alias: string,
): LocationLike | undefined {
  if (!Array.isArray(locations)) {
    return undefined;
  }
  return locations.find((location) => location.alias === alias);
}

export type StepBackgroundIssue = {
  /** Zero-based position, for programmatic use. */
  stepIndex: number;
  alias: string;
  locationTitle?: string;
  stepTypeId?: number | string;
};

/**
 * How a step is named to an author: ONE-BASED, and with its type, because "Step
 * 0" reads like a bug and a bare number is hard to find in a long node. Matches
 * the wording the publish validator uses for the same step.
 */
export function describeStep(
  stepTypes: StepTypeLike[] | undefined | null,
  stepTypeId: number | string | undefined,
  stepIndex: number,
): string {
  const stepNumber = stepIndex + 1;
  const stepType = Array.isArray(stepTypes)
    ? stepTypes.find((type) => Number(type.id) === Number(stepTypeId))
    : undefined;
  const title = (stepType as { title?: string; value?: string } | undefined);
  const name = title?.title ?? title?.value;
  return name ? `Step ${stepNumber} (${name})` : `Step ${stepNumber}`;
}

/**
 * Video steps whose per-step background resolves to a VIDEO-backed location.
 *
 * This is the same conjunction the node-level guard in `AddBranch.formValidated`
 * refuses ("Cannot use a Video Step Type with a Video Location selected"), which
 * only inspects the node's `locationId` and so cannot see a per-step override.
 * The measured runtime failure is that the scene's ambient video player is
 * destroyed mid-playback when the step's video loads, and nothing ever restores
 * it, leaving the reader on the frozen last frame of the step clip for the rest
 * of the node.
 *
 * PHASE B: when the node-level guard is relaxed, this check is the other half of
 * the same decision and must be relaxed in the same gated change, not before.
 */
export function findVideoBackgroundConflicts(
  steps: FormStepLike[] | undefined | null,
  locations: LocationLike[] | undefined | null,
  videoStepTypeId: number,
): StepBackgroundIssue[] {
  if (!Array.isArray(steps)) {
    return [];
  }

  const issues: StepBackgroundIssue[] = [];
  steps.forEach((step, stepIndex) => {
    if (!step || Number(step.stepTypeId) !== Number(videoStepTypeId)) {
      return;
    }
    const alias = readStepBackground(step.data);
    if (!alias) {
      return;
    }
    const location = findLocationByAlias(locations, alias);
    if (location && location.videoId) {
      issues.push({ stepIndex, alias, locationTitle: location.title, stepTypeId: step.stepTypeId });
    }
  });
  return issues;
}

/**
 * Steps whose per-step background names an alias that no location on the book
 * carries — normally because the location was deleted or renamed after the step
 * was authored.
 *
 * This is worth surfacing loudly: the runtime client resolves the alias directly
 * against the exported `scenes` map and reads `scenes[alias].audio` WITHOUT a
 * presence guard, so an unresolvable alias throws inside the step load rather
 * than falling back to the node's location.
 */
export function findUnresolvedBackgrounds(
  steps: FormStepLike[] | undefined | null,
  locations: LocationLike[] | undefined | null,
): StepBackgroundIssue[] {
  if (!Array.isArray(steps)) {
    return [];
  }

  const issues: StepBackgroundIssue[] = [];
  steps.forEach((step, stepIndex) => {
    if (!step) {
      return;
    }
    const alias = readStepBackground(step.data);
    if (!alias) {
      return;
    }
    if (!findLocationByAlias(locations, alias)) {
      issues.push({ stepIndex, alias, stepTypeId: step.stepTypeId });
    }
  });
  return issues;
}
